import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  createStandardPublicClientApplication,
  type AuthenticationResult,
  type IPublicClientApplication,
} from "@azure/msal-browser";
import { useCallback, useEffect, useState } from "react";

import { reportError } from "@/v1/sentry";
import config from "~/config";

const AUTHORITY = "https://login.microsoftonline.com/common";
const SCOPES = ["openid", "profile", "email"];

let clientPromise: Promise<IPublicClientApplication> | null = null;
let redirectResultPromise: Promise<AuthenticationResult | null> | null = null;

const getRedirectUri = () => `${window.location.origin}/login`;
const getPopupRedirectUri = () =>
  `${window.location.origin}/microsoft-auth-redirect.html`;

const getClient = async (): Promise<IPublicClientApplication | null> => {
  if (!config.MICROSOFT_CLIENT_ID) return null;

  if (!clientPromise) {
    clientPromise = createStandardPublicClientApplication({
      auth: {
        authority: AUTHORITY,
        clientId: config.MICROSOFT_CLIENT_ID,
        redirectUri: getRedirectUri(),
      },
      cache: {
        cacheLocation: "sessionStorage",
      },
    });
  }

  return clientPromise;
};

const getRedirectResult = async (): Promise<AuthenticationResult | null> => {
  if (!redirectResultPromise) {
    redirectResultPromise = getClient().then(
      (client) => client?.handleRedirectPromise() ?? null
    );
  }
  return redirectResultPromise;
};

const isPopupUnavailable = (error: unknown): boolean =>
  error instanceof BrowserAuthError &&
  (error.errorCode === BrowserAuthErrorCodes.popupWindowError ||
    error.errorCode === BrowserAuthErrorCodes.emptyWindowError);

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "Microsoft login failed";

export const useMicrosoftLogin = () => {
  const [idToken, setIdToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!config.MICROSOFT_CLIENT_ID) return;

    let active = true;
    getRedirectResult()
      .then((result) => {
        if (active && result?.idToken) {
          setIdToken(result.idToken);
        }
      })
      .catch((redirectError: unknown) => {
        if (!active) return;
        setError(getErrorMessage(redirectError));
        reportError(redirectError);
      });

    return () => {
      active = false;
    };
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const loginWithMicrosoft = useCallback(() => {
    setError(null);
    setIdToken(null);
    void getClient()
      .then(async (client) => {
        if (!client) return;

        try {
          const result = await client.loginPopup({
            prompt: "select_account",
            redirectUri: getPopupRedirectUri(),
            scopes: SCOPES,
          });
          setIdToken(result.idToken);
        } catch (loginError) {
          if (isPopupUnavailable(loginError)) {
            await client.loginRedirect({
              prompt: "select_account",
              redirectUri: getRedirectUri(),
              scopes: SCOPES,
            });
            return;
          }
          throw loginError;
        }
      })
      .catch((loginError: unknown) => {
        if (
          loginError instanceof BrowserAuthError &&
          loginError.errorCode === BrowserAuthErrorCodes.userCancelled
        ) {
          return;
        }
        setError(getErrorMessage(loginError));
        reportError(loginError);
      });
  }, []);

  return { clearError, error, idToken, loginWithMicrosoft };
};

export const resetMicrosoftLoginForTests = () => {
  clientPromise = null;
  redirectResultPromise = null;
};
